/**
 * ONE definition of "which signed-in person is this", for every store that has
 * to decide.
 *
 * WHY IT IS A MODULE AND NOT AN INLINE `typeof id === "number"`. The auth
 * library reads the signed-in id back out of localStorage on a cold restore —
 * where everything is a string — and hands it to `updateUser` unparsed
 * (`AuthStore.loadAuthStorage` → `updateUser({ id: loadUserId })`, which keeps
 * `id: updateUser.id || currentAuthUser.id`), so `currentUser.id` is "5", not
 * 5, for the whole window between the page loading and the first server
 * round-trip. That window is the ordinary first paint of every reload, not an
 * edge case.
 *
 * A bare `typeof id === "number"` test reads that window as "nobody is signed
 * in" — and that is far worse than it sounds, because it does not fail closed.
 * Every guard written as "is this still the person I captured?" then compares
 * null to null, finds them equal, and waves the answer through: user "1",
 * signed-out `null` and user "2" are one and the same person, so a sign-out is
 * not a change, an account switch is not a change, and the reaction that exists
 * to empty a store for the next person never fires. What is on screen is the
 * previous person's, and the next click saves it to their account.
 *
 * WHAT COUNTS AS A PERSON. A clean integer, spelled as a number or as digits,
 * is that person. Anything else — a uuid, "5.5", "1e3", an empty string, an
 * object — is nobody, which is the honest answer and keeps the guards comparing
 * like with like.
 *
 * NO LIST OF CALL SITES HERE — a comment that enumerated them is exactly what
 * goes stale. The rule instead: **an id read off the auth store goes through
 * `numericUserId`, and nothing else is a decision about who is signed in.**
 *
 *   grep -rnE 'authStore\??\.?(currentUser|user)' packages/web/src --include=*.ts --include=*.tsx
 *   grep -rnE 'typeof (id|raw|userId)[^=]*=== *"number"' packages/web/src --include=*.ts --include=*.tsx
 *
 * (plain grep, not `git grep`: some of these files are still untracked while a
 * branch is in flight, and git grep would skip them). The first finds everyone
 * who reads the id; the second finds the shape this module replaces. Anything
 * deciding WHO IS SIGNED IN that is not this function is a second definition,
 * and a second definition is the whole mechanism of this defect — it has now
 * been written three times in three stores, and fixed at one of them at a time.
 */
export const numericUserId = (raw: unknown): number | null => {
  if (typeof raw === "number") return Number.isSafeInteger(raw) ? raw : null;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

/**
 * The signed-in person on a RootStore, read the one way — `currentUser` first
 * (what every other store in this app reads) and `user`, the raw auth payload
 * behind it, as the fallback.
 *
 * ALWAYS CALLED, NEVER A COMPUTED. `makeAutoObservable` turns a getter into a
 * computed and a `reaction` keeps that computed alive, so a store that exposed
 * this as a getter would report the FIRST signed-in person it ever saw, for
 * ever — which is precisely the account-switch bug the callers use it to catch.
 * The auth store is not the caller's own observable state, so nothing would
 * ever invalidate it.
 */
export const currentUserIdOf = (rootStore: unknown): number | null => {
  const auth = (
    rootStore as {
      authStore?: { currentUser?: { id?: unknown }; user?: { id?: unknown } };
    } | null
  )?.authStore;
  return numericUserId(auth?.currentUser?.id ?? auth?.user?.id);
};
